import { Col, Row, Space } from "antd";
import locale from "antd/es/date-picker/locale/ko_KR";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getHolidays } from "../../../../apis/ReservationConfig/apis";
import { theme } from "../../../../theme";
import { CalendarHeader } from "./Header";
import { CustomCalendar, DateBox, Days, HolidayName } from "./index.styled";
import { Dot } from "../../Icon";

interface BooksCalendarProps {
  disableDates?: moment.Moment[];
  current?: moment.Moment;
  onChange?: (date: moment.Moment) => void;
}

/**
 * 장착점 휴무 등 일정 캘린더
 * @param param0
 * @returns
 */
export const BooksCalendar: React.FC<BooksCalendarProps> = ({
  current = undefined,
  disableDates = [],
  onChange = () => void 0,
}) => {
  const [year, setYear] = useState(moment().format("YYYY"));
  const [month, setMonth] = useState(moment().format("MM"));

  const [selectedDate, setSelectedDate] = useState<moment.Moment | undefined>(
    current
  );

  /**
   * 월별 공휴일 정보 조회
   */
  const { data: holidayData } = useQuery(
    ["Holiday", year, month],
    () =>
      getHolidays({
        publicYear: year,
        publicMonth: month,
      }),
    {
      keepPreviousData: true,
      select: (data) =>
        data.rows.map((date) => ({
          date: moment(date.publicHoliday),
          name: date.publicHolidayName,
        })),
    }
  );

  /**
   *
   * @param date 캘린더 선택
   */
  const handleChange = (date: moment.Moment) => {
    setSelectedDate(date);
    onChange(date);
  };

  /**
   * 년월 변경 로직 처리
   * @param date
   * @param mode
   */
  const handlePanelChange = (date: moment.Moment) => {
    const y = date.format("YYYY");
    const m = date.format("MM");
    if (year !== y) {
      setYear(date.format("YYYY"));
    }
    if (month !== m) {
      setMonth(date.format("MM"));
    }
  };

  useEffect(() => {
    if (current) {
      setSelectedDate(current);
    }
  }, [current]);

  return (
    <Row justify="space-between">
      <Col span={24}>
        <CustomCalendar
          fullscreen={false}
          locale={locale}
          headerRender={({ value, onChange }) => {
            const current = value ? value.clone() : undefined;

            return <CalendarHeader value={current} onChange={onChange} />;
          }}
          onChange={handleChange}
          onPanelChange={handlePanelChange}
          dateFullCellRender={(current) => {
            let color = "";

            /**
             * 일요일 처리
             */
            if (current.format("MM") === month) {
              const currentDay = current.day();
              if (currentDay === 0) {
                color = theme.colors.red3;
              } else if (currentDay === 6) {
                color = theme.colors.blue2;
              }
            }
            /**
             * 공휴일 처리
             */
            const holiday = holidayData?.find(({ date }) =>
              date.isSame(current, "date")
            );
            if (holiday) {
              color = theme.colors.red3;
            }

            const today = current.isSame(moment(), "date");

            return (
              <DateBox
                style={{ color, display: "inline-flex", alignItems: "center" }}
                className={`ant-picker-cell-inner ${color ? "color" : ""}`}
              >
                <Days>{current.get("date")}</Days>
                <div style={{ position: "absolute", bottom: 0 }}>
                  {holiday && <HolidayName>{holiday.name}</HolidayName>}
                  {today && (
                    <HolidayName>
                      <Dot
                        fill={theme.colors.mainPrimary3}
                        width={14}
                        height={14}
                      />
                    </HolidayName>
                  )}
                </div>
              </DateBox>
            );
          }}
          disabledDate={(current) => {
            if (disableDates && disableDates.length > 0) {
              const bookable = disableDates?.find((date) =>
                moment(date).isSame(current, "date")
              );

              return !bookable;
            }
            return false;
          }}
          value={selectedDate}
        />
      </Col>
    </Row>
  );
};
